import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BaseKendoList } from '@common/classes/base-kendo-list';
import { GridColumnConfigController } from '@common/classes/grid-column-config-controller';
import { ConfigurableGridColumn } from '@common/components/column-configurator/column-configurator.component';
import { ManipulationCodes } from '@common/knownTypes/ManipulationCodes';
import { PagingAndOrderingModel } from '@common/models/common/PagingAndOrdering.model';
import { NavisService } from '@common/services/navis.service';
import { ServiceRequestService } from '@common/services/service-request.service';
import { marker } from '@common/translate-marker';
import { requiredValidator } from '@common/validators/required-validator';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { GridComponent, RowArgs, RowClassArgs, SelectableSettings } from '@progress/kendo-angular-grid';
import { ToastService } from '@services/toast.service';
import { ContainerListFilter } from 'app/navis/models/ContainerListFilter';
import { BaseKendoListService } from 'app/singleton-services/base-kendo-list.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, shareReplay } from 'rxjs';

export interface NavisVesselVisitsByCallSign {
    vesselVisits: NavisVesselVisitByCallSign[];
}

export interface NavisVesselVisitByCallSign {
    visit: string;
    ibVyg: string;
    obVyg: string;
    vesselName: string;
    phase: string;
    line: string;
    eta: string;
    ata: string;
    etd: string;
    atd: string;
    inboundCaptain: string;
}
@Component({
    selector: 'app-add-containers-from-tos',
    templateUrl: './add-containers-from-tos.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddContainersFromTosComponent extends BaseKendoList implements OnInit, AfterViewInit {
    override applyStateFilter: boolean = false;
    isBusy = false;
    filter = new ContainerListFilter();
    location: Location;
    public title: string;
    public message: string;
    public cargoManifestId: number;
    public nominatedAgentShippingLineId: number;
    public serviceRequestId: number;
    public manipulationId: string;
    public notificationTypeId: string;
    public fromServiceRequest: boolean;
    public shipmentType: string;
    public tosVoyageNumber: string;
    public portVoyageNumber: string;
    public agentId: number;
    public customerId: number;
    public yesButtonName: string;
    public noButtonName: string;
    public cancelButtonName: string;
    public formReady: boolean = false;
    public showBookingOrBLAlert: boolean = false;
    public isVesselAgent: boolean = false;
    public hasVesselContainerLineBypass: boolean = false;
    public matchingShippingLines: any;
    public cargoManifestTypeId: string;
    public vesselName: string;
    public vesselCallSign: string;

    itemsGridView;
    autoLoadData = false;

    line$: Observable<any>;
    lines: any[] = [];
    eitherBookingOrBLValidaorRef = this.eitherBookingOrBLValidator();

    @ViewChild(GridComponent) public grid: GridComponent;

    selected: any[] = [];
    public selectableSettings: SelectableSettings;

    configurableColumns: ConfigurableGridColumn[] = [
        { key: 'articleNumber', title: marker('Article Number') },
        { key: 'unitNumber', title: marker('Unit Number') },
        { key: 'lastMove', title: marker('Last Move') },
        { key: 'typeIso', title: marker('ISO Type') },
        { key: 'category', title: marker('Category') },
        { key: 'frightKind', title: marker('Freight Kind') },
        { key: 'hazardous', title: marker('Hazardous') },
        { key: 'lineOp', title: marker('Line Operator') },
        { key: 'ibActualVisit', title: marker('I/B Actual Visit') },
        { key: 'obActualVisit', title: marker('O/B Actual Visit') },
        { key: 'vState', title: marker('V-state') },
        { key: 'tState', title: marker('T-state') },
        { key: 'position', title: marker('Position') },
        { key: 'cmdy', title: marker('Cmdy') },
        { key: 'bookingNumber', title: marker('Booking Nbr.') },
        { key: 'blNbr', title: marker('Bill of Lading nbr') },
        { key: 'portOfDischargeId', title: marker('POD') },
        { key: 'shipper', title: marker('Shipper') },
        { key: 'consignee', title: marker('Consignee') },
        { key: 'timeIn', title: marker('Time in') },
        { key: 'timeOut', title: marker('Time out') },
        { key: 'netWeight', title: marker('Net Weight (kg)') },
        { key: 'weight', title: marker('Weight (kg)') },
        { key: 'vgmWeight', title: marker('VGM Weight (kg)') },
        { key: 'typeHeight', title: marker('Type Height') },
        { key: 'typeLength', title: marker('Type Length') },
        { key: 'damageSeverity', title: marker('Damage Severity') },
        { key: 'reqsPower', title: marker('Reqs. power') },
        { key: 'stopVsl', title: marker('Stop-Vessel') },
        { key: 'stopRail', title: marker('Reqs. power') },
        { key: 'stopRoad', title: marker('Stop-Road') },
        { key: 'unNumberId', title: marker('Hzd UNNbrs') },
        { key: 'portOfLoadingId', title: marker('POL') },
        { key: 'sealNbr1', title: marker('Seal Nbr1') },
        { key: 'temperatureRange', title: marker('Temperature range') },
        { key: 'nbrOfPackages', title: marker('Nbr. of Packages'), isAvailable: () => this.isGetCtnMode },
        { key: 'packageType', title: marker('Pack. Type'), isAvailable: () => this.isGetCtnMode }
    ];
    readonly columnConfig = new GridColumnConfigController(
        'common.addContainersFromTos',
        () => this.configurableColumns,
        () => this.grid,
        () => this.refreshColumnLayout()
    );

    constructor(
        baseKendoListService: BaseKendoListService,
        public activeModal: DialogRef,
        private navisService: NavisService,
        private cdr: ChangeDetectorRef,
        private serviceRequestService: ServiceRequestService,
        private toastyService: ToastService,
        private spinner: NgxSpinnerService
    ) {
        super(baseKendoListService);
        this.location = location;

        this.setSelectableSettings();
    }

    public get isAllSelected() {
        return this.selected.length == this.itemsGridView?.data?.length;
    }

    get isGetCtnMode(): boolean {
        return this.notificationTypeId !== 'Arrival' && this.notificationTypeId !== 'Departure';
    }

    ngOnInit(): void {
        this.columnConfig.refresh();
        super.ngOnInit();
    }

    ngAfterViewInit(): void {
        this.filter = new ContainerListFilter();

        this.line$ = this.serviceRequestService
            .getLinesForContainers(this.agentId, this.cargoManifestId, this.serviceRequestId)
            .pipe(shareReplay(1));

        this.processLinesObservable(this.line$);
        this.populateInitialValues();
        this.cdr.detectChanges();
    }

    public getDefaultFilter(): ContainerListFilter {
        const pageAndFilterObj = new PagingAndOrderingModel(this.pageObj.skip, this.pageObj.take);
        const filter = new ContainerListFilter(pageAndFilterObj, this.form.getRawValue());

        filter.cargoManifestId = this.cargoManifestId;
        filter.serviceRequestId = this.serviceRequestId;

        return filter;
    }

    public listQuery(filter: ContainerListFilter) {
        this.isBusy = true;
        this.selected = [];
        filter.agentId = this.agentId;
        filter.cargoManifestId = this.cargoManifestId;
        filter.serviceRequestId = this.serviceRequestId;
        filter.line = this.form.controls['line'].value;
        return this.navisService.getNavisContainersForVisit(this.notificationTypeId, filter);
    }

    protected override defaultSort() {
        this.sortsArr = [{ field: 'articleNumber', dir: 'asc' }];
    }

    public selectionCallback(context: RowArgs) {
        return context.dataItem.unitNumber;
    }

    override searchForm(): void {
        // if the manipulationId is not VSIN or VSOUT, then do normal search
        if (!['VSOUT', 'VSIN'].includes(this.manipulationId)) {
            this.resetPaging();
            this.search(this.form, this.listQuery(this.filter));
            return;
        }

        this.spinner.show();
        // perform vessel validation. First get all vessel visits for the call sign
        // then check if any of them match the vessel name and voyage number
        // if not, show error and do not perform search
        this.navisService.getNavisVesselVisitsByCallSign(this.vesselCallSign).subscribe({
            next: (res: NavisVesselVisitsByCallSign) => {
                if (!res || !res.vesselVisits?.length) {
                    this.toastyService.error(
                        this.translateService.instant('Voyage number missing or invalid. Please verify.')
                    );
                    this.spinner.hide();
                    return;
                }
                // voyage number must match either the visit id or the inbound(VSIN) / outbound(VSOUT) voyage number
                const voyageNumber = this.form.controls['vesselVisitVoyage'].value;
                const matchingRecords = res.vesselVisits.some(
                    (x) =>
                        x.visit === voyageNumber ||
                        (this.manipulationId === ManipulationCodes.loadingVessel
                            ? x.obVyg === voyageNumber
                            : x.ibVyg === voyageNumber)
                );

                if (!matchingRecords) {
                    this.toastyService.error(
                        this.translateService.instant('Please confirm the voyage number is correct.')
                    );
                    this.spinner.hide();
                    return;
                }

                // continue with search
                this.resetPaging();
                this.search(this.form, this.listQuery(this.filter));
                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
            }
        });
    }

    populateInitialValues() {
        if (this.notificationTypeId != null) {
            this.manipulationId = this.notificationTypeId == 'Arrival' ? 'VSIN' : 'VSOUT';

            if (this.serviceRequestId || this.cargoManifestId) {
                this.form.controls['vesselVisitVoyage'].setValidators(requiredValidator());
            }
        }

        if (this.tosVoyageNumber) {
            this.form.controls['vesselVisitVoyage'].setValue(this.tosVoyageNumber);
            this.filter.vesselVisitVoyage = this.tosVoyageNumber;
        } else {
            this.form.controls['vesselVisitVoyage'].setValue(this.portVoyageNumber);
            this.filter.vesselVisitVoyage = this.portVoyageNumber;
        }

        this.form.controls['manipulationId'].setValue(this.manipulationId);
        this.filter.manipulationId = this.manipulationId;

        this.form.controls['operatorId'].setValue('BMF');
        this.filter.operatorId = 'BMF';

        this.form.controls['terminal'].setValue('BURGAS');
        this.filter.terminal = 'BURGAS';

        if (this.shipmentType != null) {
            const shipType = this.shipmentType == 'FULLCNT' ? 'F' : 'E';
            this.form.controls['fe'].setValue(shipType);
            this.filter.fe = shipType;
            this.form.controls['fe'].disable();
        }

        if (this.nominatedAgentShippingLineId) {
            this.form.controls['line'].disable();
        }

        if (this.notificationTypeId !== 'Arrival' && this.notificationTypeId !== 'Departure') {
            this.form.controls['containers'].setValidators(requiredValidator());
            this.form.controls['containers'].updateValueAndValidity();
        }

        if (!this.hasVesselContainerLineBypass && this.user.organization['id'] != this.agentId) {
            this.form.controls['line'].setValidators(requiredValidator());
            this.form.controls['line'].updateValueAndValidity();
        }

        this.form.setValidators(this.eitherBookingOrBLValidaorRef);
        this.showBookingOrBLAlert = true;

        if (!this.fromServiceRequest) {
            this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
            this.showBookingOrBLAlert = false;
        }

        if (this.manipulationId === 'VSIN' || this.cargoManifestTypeId === 'ACM') {
            this.form.controls['pod'].setValue('BOJ');
        } else if (this.manipulationId === 'VSOUT') {
            this.form.controls['pol'].setValue('BOJ');
        }

        this.form.updateValueAndValidity({ emitEvent: true });
    }

    save() {
        this.activeModal.close(this.fetchSelectedItems());
    }

    clear() {
        this.form.reset();
        this.populateInitialValues();
        this.selected = [];
    }

    fetchSelectedItems(): any {
        const res = [];

        this.selected.forEach((x) => {
            const item = this.itemsGridView.data.filter((f) => f.unitNumber == x).pop();
            if (item) res.push(item);
        });

        return { cargoManifestId: this.cargoManifestId, items: res };
    }

    notSameAgent() {
        if (this.hasVesselContainerLineBypass) {
            return false;
        }

        if (this.fromServiceRequest) {
            return (
                (this.form.controls['line'].value == null || this.form.controls['line'].value == '') &&
                this.customerId != this.agentId
            );
        } else {
            return (
                (this.form.controls['line'].value == null || this.form.controls['line'].value == '') &&
                this.user.organization['id'] != this.agentId
            );
        }
    }

    differentBillOfLadingAgent() {
        if (!this.showBookingOrBLAlert) {
            return false;
        }

        if (
            this.manipulationId == ManipulationCodes.unloadingVessel ||
            this.manipulationId == ManipulationCodes.TROUT ||
            this.manipulationId == ManipulationCodes.RLOUT
        ) {
            if (this.fromServiceRequest) {
                return (
                    (this.form.controls['billOfLading'].value == null ||
                        this.form.controls['billOfLading'].value == '') &&
                    this.customerId != this.agentId
                );
            } else {
                return (
                    (this.form.controls['billOfLading'].value == null ||
                        this.form.controls['billOfLading'].value == '') &&
                    this.user.organization['id'] != this.agentId
                );
            }
        }

        return false;
    }

    // check currently selected rows when switching pages
    isRowSelected = (e: RowArgs) => this.selected.indexOf(e.dataItem.unitNumber) >= 0;

    clearSelection() {
        this.selected = [];
    }

    rowCallback = (context: RowClassArgs) => {
        const isSelected = this.selected.indexOf(context.dataItem.unitNumber) >= 0;
        return {
            blueColoredRow: isSelected,
            uncolored: !isSelected
        };
    };

    toggleAll() {
        const isAllSelected = this.isAllSelected;
        this.selected = [];

        if (!isAllSelected) {
            this.itemsGridView.data.forEach((x) => {
                this.selected.push(x.unitNumber);
            });
        }
    }

    eitherBookingOrBLValidator(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {
            const booking = group.controls['booking'].value;
            const billOfLading = group.controls['billOfLading'].value;

            if ((booking && !billOfLading) || (!booking && billOfLading) || (booking && billOfLading)) {
                return null;
            } else {
                return { eitherBookingOrBillOfLading: true };
            }
        };
    }

    containersEmptyForArrivalOrDeparture() {
        return (
            this.notificationTypeId !== 'Arrival' &&
            this.notificationTypeId !== 'Departure' &&
            !this.form.controls['containers'].value
        );
    }

    processLinesObservable(inputObservable: Observable<any>): Observable<any> {
        inputObservable.subscribe((value) => {
            this.lines = value;

            if (!this.fromServiceRequest) {
                this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
                this.showBookingOrBLAlert = false;
            } else {
                if (!this.hasVesselContainerLineBypass) {
                    this.form.controls['line'].valueChanges.subscribe((value) => {
                        if (value) {
                            const changedLine = this.lines.find((obj) => obj.id === value);
                            if (changedLine && this.matchingShippingLines.includes(changedLine.id)) {
                                this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
                                this.showBookingOrBLAlert = false;
                            } else {
                                this.form.setValidators(this.eitherBookingOrBLValidaorRef);
                                this.showBookingOrBLAlert = true;
                            }
                        } else {
                            this.form.setValidators(this.eitherBookingOrBLValidaorRef);
                            this.showBookingOrBLAlert = true;
                        }
                    });
                } else {
                    this.form.removeValidators(this.eitherBookingOrBLValidaorRef);
                    this.showBookingOrBLAlert = false;
                }
            }

            this.form.updateValueAndValidity({ emitEvent: true });
        });

        return inputObservable;
    }
}
